import {
  P2GBox,
  P2GIcon,
  P2GTooltip,
  SvgActions,
  SvgAddCircleBlock,
  SvgCheckCircle,
  SvgCrossCircle
} from '@parcel2go/core-ui';
import { useCallback, useContext, useMemo } from 'react';
import { ExtraBase, QuoteExtraApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ToTranslatedExtra } from '~/apps/smartsend/lib/helpers/ToUniqueExtras';
import { ExtrasIcon, Oversize, ProtectionExtraIds } from '../../ExtrasHelper';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { getSelectedQuoteOptionForItem } from '../generateProtectionKey';
import { SmartSendContext } from '~/apps/smartsend/lib/context/SmartSendContext';
import { ApplierOption } from '~/apps/smartsend/lib/helpers/ApplierOption';
import { SaveItemsAction } from '~/apps/smartsend/lib/context/SmartSendContextModels';
import { ExtrasHaveChanged } from '~/apps/smartsend/lib/helpers/SmartSendSocketHelper';
import { EditType } from './EditType';
import { SidePanelContent } from '~/components/sidepanel-section';
import { DeliveredDutyPaidExtra } from '~/lib/consts/extras';
import { ItemExtrasTooltipContent } from './ItemExtrasTooltipContent';
import { EventSections } from '~/apps/smartsend/lib/events/EventLogger';
import ItemProtectionSummaryCheck from './ItemProtectionSummaryCheck';
import ItemCustomsQuickLabel from './ItemCustomsQuickLabel';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { ToSmartSendAnalyticsModel, ToSmartSendAnalyticsPatchModel } from '../quotes/utils/CurrentSelectionType';

interface props {
  item: SmartSendItemApiModel;
  edit?: (type: EditType) => void;
  isMobile: boolean;
}

const ExtrasToHide = [...ProtectionExtraIds, Oversize];
const ExtrasToHidePricing = [DeliveredDutyPaidExtra];

export const ExtrasToggle = ({
  e,
  onToggleExtra,
  selected
}: {
  e: ExtraBase & { Available: boolean };
  selected: boolean;
  onToggleExtra?: (extra: ExtraBase & { Available: boolean }) => void;
}) => (
  <P2GBox
    sx={{
      position: 'relative',
      cursor: 'pointer',
      opacity: e.Available ? 1 : 0.15,
      filter: e.Available ? 'none' : 'grayscale(100%)',
      display: 'inline-flex'
    }}
    onClick={() => onToggleExtra?.(e)}
  >
    <P2GIcon size="xs" palette="primary">
      <ExtrasIcon Id={e.Id} />
    </P2GIcon>
    <P2GBox
      sx={{
        position: 'absolute',
        bottom: '-13px',
        right: '-10px',
        scale: 0.8,
        backgroundColor: 'background.paper'
      }}
    >
      <P2GIcon size="xs">
        {e.Available ? selected ? <SvgCheckCircle /> : <SvgAddCircleBlock /> : <SvgCrossCircle />}
      </P2GIcon>
    </P2GBox>
  </P2GBox>
);

const ExtraDisplay = ({
  item,
  e,
  onToggleExtra,
  isMobile,
  wrapper
}: {
  item: SmartSendItemApiModel;
  extras: ExtraBase[];
  isMobile;
  e: QuoteExtraApiModel & { Available: boolean; TranslatedName: string; Price: number };
  wrapper: (content: React.ReactNode, selected: boolean) => React.ReactNode;
  onToggleExtra: (extra: ExtraBase & { Available: boolean }) => void;
}) => {
  const selected = item.Extras.some((x) => x.Id === e.Id);
  return wrapper(
    isMobile ? (
      <ExtrasToggle e={e} selected={selected} onToggleExtra={onToggleExtra} />
    ) : (
      <P2GTooltip
        variant="light"
        title={
          <SidePanelContent>
            <ItemExtrasTooltipContent
              selected={selected}
              extra={e}
              shouldShowPricing={e.Available && !selected && !ExtrasToHidePricing.includes(e.Id)}
            />
          </SidePanelContent>
        }
      >
        <ExtrasToggle e={e} selected={selected} onToggleExtra={onToggleExtra} />
      </P2GTooltip>
    ),
    selected
  );
};

export default function ItemExtrasToggle({ item, edit, isMobile }: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { dispatch } = useContext(SmartSendContext);
  const { track } = useSmartSendAnalytics();

  const onSave = useCallback(
    async (extras: ExtraBase[]) => {
      const actions: ApplierOption[] = [];

      if (ExtrasHaveChanged(item.Extras, extras)) {
        actions.push('Extras');
      }

      if (actions.length > 0) {
        // Only add the new extras to the patch model.
        const newExtras = extras.filter((e) => !item.Extras.some((x) => x.Id === e.Id)).map((e) => e.Id);
        const removedExtras = item.Extras.filter((e) => !extras.some((x) => x.Id === e.Id)).map((e) => e.Id);
        const patchModel = ToSmartSendAnalyticsPatchModel({ Extras: newExtras }, actions);

        track({
          type: 'item_edited',
          payload: {
            ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.SmartSendItem]),
            surface: 'item-extras-toggle',
            action: 'save-item-edits',
            changedFields: actions,
            itemScope: 'single',
            addedExtraIds: newExtras,
            removedExtraIds: removedExtras,
            serviceId: item.Service?.Id ?? null,
            serviceProvider: item.Service?.Provider ?? null,
            originalItems: [ToSmartSendAnalyticsModel(item)],
            patchModel: patchModel
          }
        });
      }

      dispatch(
        new SaveItemsAction([item.Id], actions, {
          Extras: extras
        })
      );
    },
    [dispatch, item, track]
  );

  const onExtraAdd = useCallback(
    (extra: ExtraBase) => {
      const newExtras = [...item.Extras, extra];
      onSave(newExtras);
    },
    [onSave, item.Extras]
  );

  const onExtraRemove = useCallback(
    (extra: ExtraBase) => {
      const newExtras = item.Extras.filter((x) => x.Id !== extra.Id);
      onSave(newExtras);
    },
    [onSave, item.Extras]
  );

  const onToggleExtra = useCallback(
    (extra: ExtraBase & { Available: boolean }) => {
      if (!extra.Available) {
        return;
      }

      if (extra.Id === DeliveredDutyPaidExtra) {
        edit('CUSTOMS');
        return;
      }

      if (item.Extras.some((x) => x.Id === extra.Id)) {
        onExtraRemove(extra);
      } else {
        onExtraAdd(extra);
      }
    },
    [onExtraAdd, onExtraRemove, edit, item.Extras]
  );

  const availableExtras = useMemo(() => {
    if (item.MarketplaceStatus !== 'Sold') {
      return [];
    }

    const selectedQuote = getSelectedQuoteOptionForItem(item);
    if (!selectedQuote) {
      return [];
    }

    const uniqueExtras = selectedQuote.AvailableExtras.concat(
      selectedQuote.Extras.filter((x) => !selectedQuote.AvailableExtras.some((a) => a.Id === x.Id))
    ).map((x) => ({ ...x, Available: true, TranslatedName: x.Name }));

    SettingsCache.settings.Features.filter((x) => x.startsWith('Extra:'))
      .map((x) => x.replace('Extra:', ''))
      .forEach((extraId) => {
        if (!uniqueExtras.some((x) => x.Id === extraId)) {
          uniqueExtras.push({
            Id: extraId,
            Price: 0,
            Vat: 0,
            Total: 0,
            Name: extraId,
            Available: false,
            TranslatedName: extraId
          });
        }
      });

    return uniqueExtras
      .filter((x) => !ExtrasToHide.includes(x.Id))
      .map((x) => ({
        ...x,
        TranslatedName: ToTranslatedExtra(t, x)
      }))
      .sort((a, b) => a.TranslatedName.localeCompare(b.TranslatedName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, item.State?.validation?.Validated, item.State?.validation?.QuoteState?.Quote?.Service, t]);

  if (
    item.MarketplaceStatus !== 'Sold' ||
    item.Status !== 'Active' ||
    !SettingsCache.settings.Features.includes('Extras')
  ) {
    return null;
  }

  const ddpExtra = availableExtras?.find((x) => x.Id === DeliveredDutyPaidExtra);

  return (
    <P2GBox sx={{ gap: 3, alignItems: 'center', display: 'flex', flexWrap: 'nowrap', justifyContent: 'space-between' }}>
      <P2GBox display="flex" alignItems="center" gap={0.5}>
        {availableExtras
          .filter((x) => x.Id !== DeliveredDutyPaidExtra)
          .map((e) => (
            <ExtraDisplay
              key={e.Id}
              item={item}
              isMobile={isMobile}
              e={e}
              onToggleExtra={onToggleExtra}
              extras={item.Extras}
              wrapper={(x) => <P2GBox pl={1}>{x}</P2GBox>}
            />
          ))}
      </P2GBox>

      <ItemCustomsQuickLabel
        item={item}
        ddpExtra={ddpExtra}
        isMobile={isMobile}
        wrapper={(x) => <P2GBox>{x}</P2GBox>}
        edit={edit}
      />

      {item.MarketplaceStatus === 'Sold' && (
        <ItemProtectionSummaryCheck
          item={item}
          edit={edit}
          isMobile={isMobile}
          section={EventSections.SmartSendItem}
          wrapper={(content) => <P2GBox>{content}</P2GBox>}
        />
      )}

      {edit && !isMobile && (
        <P2GBox>
          <span title={t('EDIT_EXTRAS', { defaultValue: 'Edit Extras' })}>
            <P2GIcon size="xs" palette="primary" onClick={() => edit('EXTRAS')}>
              <SvgActions />
            </P2GIcon>
          </span>
        </P2GBox>
      )}
    </P2GBox>
  );
}
